import React, { useEffect, useMemo, useState } from "react";
import { concat, get, merge, uniq } from "lodash";
import {
  IBase,
  IGetValue,
  IInput,
  ILabel,
  ILayout,
  IModel,
  IName,
  IRadio,
  ISelect,
  ISelectOption,
  IUpload,
  ILocationValue,
  ILocationMatrixValue,
} from "components/Template/types";
import { format } from "components/Template/action";
import { fetchOptions, isQueryReady } from "components/Template/web";
import "./PrintableItem.scss";

interface PrintableProps {
  template: IInput | ILayout<IBase>;
  hiddenList: Array<IName>;
  getValue: IGetValue;
  model: IModel;
  listContext?: { name: IName; index: number; itemLabel?: string };
}

const EMPTY_SELECT_OPTIONS: Array<ISelectOption> = [];
const PRINTABLE_LAYOUT_TYPES = ["group", "tabs", "tab", "explorer", "anchors", "list"];

const isPrintableLayoutType = (type: string) => PRINTABLE_LAYOUT_TYPES.includes(type);

const findOptionLabel = (options: Array<{ value: string | number; label: string }>, value: unknown) =>
  options.find((option) => String(option.value) === String(value) || String(option.label) === String(value))?.label;

const getSelectQueryModeledKey = (template: ISelect | undefined, getValue: IGetValue): string => {
  if (!template?.query) {
    return "";
  }
  const modeled = uniq(
    concat(
      get(template, ["query", "url", "sources"], []),
      get(template, ["query", "body", "sources"], []),
      get(template, ["query", "transform", "sources"], [])
    )
  ).reduce((m, k) => merge(m, { [k]: getValue(k) }), {});
  return JSON.stringify(modeled);
};

const renderValue = (
  template: IInput | ILayout<IBase>,
  getValue: IGetValue,
  model: IModel,
  selectOptions: Array<ISelectOption>
) => {
  switch (template.type) {
    case "file":
      return (getValue(template.name) as IUpload).filename;
    case "checkbox":
      return getValue(template.name) ? "Enabled" : "Not Enabled";
    case "select":
      return findOptionLabel(selectOptions, getValue(template.name)) || getValue(template.name);
    case "radio":
      return findOptionLabel((template as IRadio).options || EMPTY_SELECT_OPTIONS, getValue(template.name)) || getValue(template.name);
    case "range":
      const range = getValue(template.name) as [number, number];
      return `${range[0]} - ${range[1]}`;
    case "location":
      return ((getValue(template.name) as ILocationValue | undefined)?.label || "Not selected");
    case "location_matrix": {
      const matrix = getValue(template.name) as ILocationMatrixValue | undefined;
      return matrix?.pairs?.length ? `${matrix.pairs.length} unique driving routes` : "Not calculated";
    }
    case "label":
      return (template as ILabel).format
        ? format(model, (template as ILabel).format as NonNullable<ILabel["format"]>, getValue)
        : getValue(template.name);
    default:
      return getValue(template.name);
  }
};

const renderItem = (
  template: IInput | ILayout<IBase>,
  value: React.ReactNode,
  isLoadingValue: boolean,
  children: React.ReactNode,
  listContext?: { name: IName; index: number; itemLabel?: string }
) => {
  const renderLabel = () => {
    if (listContext?.itemLabel) {
      return `${listContext.itemLabel} ${listContext.index + 1}${template.label ? ` - ${template.label}` : ''}`;
    }
    return template.label;
  };

  if (isPrintableLayoutType(template.type)) {
    return (
      <div className={`level-printable anchor-${template.name}`} id={template.name}>
        <div className="layout-label">{renderLabel()}</div>
        <>{children}</>
      </div>
    );
  } else {
    return (
      <div className={`individual-item anchor-${template.name}`} id={template.name}>
        <div className="label">{renderLabel()}</div>
        <div className="value">
          {isLoadingValue ? (
            <span className="printable-value-skeleton" aria-hidden="true" />
          ) : (
            <>{value} {template.info}</>
          )}
        </div>
      </div>
    );
  }
};

export default function PrintableItem(props: React.PropsWithChildren<PrintableProps>) {
  const { template, hiddenList, getValue, model, children, listContext } = props;
  const selectTemplate = template.type === "select" ? template as ISelect : undefined;
  const staticSelectOptions = selectTemplate?.options || EMPTY_SELECT_OPTIONS;
  const selectedValue = selectTemplate ? getValue(selectTemplate.name) : undefined;
  const hasSelectedValue = selectedValue !== undefined && selectedValue !== null && selectedValue !== "";
  const hasStaticOption = Boolean(findOptionLabel(staticSelectOptions, selectedValue));
  const shouldLoadOptions = Boolean(
    selectTemplate?.query &&
    hasSelectedValue &&
    !hasStaticOption &&
    isQueryReady(model, selectTemplate.query, getValue)
  );
  const modeledKey = useMemo(
    () => getSelectQueryModeledKey(selectTemplate, getValue),
    [selectTemplate, getValue]
  );
  const [selectOptions, setSelectOptions] = useState<Array<ISelectOption>>(staticSelectOptions);
  const [isLoadingValue, setIsLoadingValue] = useState(shouldLoadOptions);

  useEffect(() => {
    if (!selectTemplate?.query || !shouldLoadOptions) {
      setSelectOptions(staticSelectOptions);
      setIsLoadingValue(false);
      return;
    }

    let active = true;
    setIsLoadingValue(true);
    fetchOptions(model, selectTemplate.query, getValue)
      .then((options) => {
        if (active) {
          setSelectOptions(options);
        }
      })
      .catch(() => {
        if (active) {
          setSelectOptions(staticSelectOptions);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoadingValue(false);
        }
      });

    return () => {
      active = false;
    };
    // Re-fetch only when query source values change, not when getValue/model identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeledKey, shouldLoadOptions, staticSelectOptions]);

  if (!template.label && !listContext?.itemLabel) {
    return <>{children}</>;
  }

  if (template.type === "tabs") {
    return <>{children}</>;
  }

  if (hiddenList?.includes(template.name)) {
    return <></>;
  }

  const value = renderValue(template, getValue, model, selectOptions);
  return renderItem(template, value, isLoadingValue, children, listContext);
}
